import React, { useEffect, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loading, Empty, Row, KPI } from "./_shared";

export function ReceiptBadge({ req }) {
  const r = req?.receipt;
  if (req?.status !== "paid") {
    return <span className="inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-wider bg-slate-100 text-slate-500">n/a</span>;
  }
  if (r?.confirmed) {
    return <span className="inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-wider bg-emerald-100 text-emerald-800">received</span>;
  }
  return <span className="inline-block rounded px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-wider bg-amber-100 text-amber-800">awaiting</span>;
}

export function ReceiptConfirmBlock({ request, onConfirmed }) {
  const r = request?.receipt;
  const [form, setForm] = useState({ method: "bank_transfer", reference: "", received_on: new Date().toISOString().slice(0, 10), signature: "", notes: "" });
  const [busy, setBusy] = useState(false);

  if (r?.confirmed) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-xs">
        <div className="font-semibold text-emerald-900 mb-1">Receipt Confirmed</div>
        <div className="grid grid-cols-2 gap-2 text-emerald-900/90">
          <div><span className="text-emerald-700/70">Method:</span> {r.method?.replace(/_/g, " ")}</div>
          <div><span className="text-emerald-700/70">Reference:</span> {r.reference || "—"}</div>
          <div><span className="text-emerald-700/70">Received on:</span> {r.received_on}</div>
          <div><span className="text-emerald-700/70">Signature:</span> {r.signature || "—"}</div>
          <div className="col-span-2"><span className="text-emerald-700/70">Confirmed by:</span> {r.confirmed_by} · {new Date(r.confirmed_at).toLocaleString()}</div>
          {r.notes && <div className="col-span-2"><span className="text-emerald-700/70">Notes:</span> {r.notes}</div>}
        </div>
      </div>
    );
  }

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/hr/salary-requests/${request.id}/confirm-receipt`, form);
      toast.success("Payroll receipt recorded");
      onConfirmed?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-xs space-y-2" data-testid="receipt-confirm-block">
      <div className="font-semibold text-amber-900">Payroll Receipt — awaiting confirmation</div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px]">Method *</Label>
          <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
              <SelectItem value="mobile_money">Mobile Money</SelectItem>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="cheque">Cheque</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-[10px]">Reference / Txn ID</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
        <div><Label className="text-[10px]">Received on</Label><Input type="date" value={form.received_on} onChange={(e) => setForm({ ...form, received_on: e.target.value })} /></div>
        <div><Label className="text-[10px]">Signature</Label><Input placeholder="Typed name" value={form.signature} onChange={(e) => setForm({ ...form, signature: e.target.value })} /></div>
        <div className="col-span-2"><Label className="text-[10px]">Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
      </div>
      <Button onClick={submit} disabled={busy} className="btn-primary-blue w-full" data-testid="receipt-confirm-submit">
        {busy ? "Saving…" : "Confirm Receipt"}
      </Button>
    </div>
  );
}

export default function PayrollReceipts({ staff }) {
  const [data, setData] = useState({ items: [], total: 0, confirmed: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const staffName = (id) => staff.find((x) => x.id === id)?.full_name || id;

  const load = async () => {
    setLoading(true);
    try { setData((await api.get("/hr/payroll/receipts")).data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = data.items.filter((r) => {
    if (filter === "confirmed") return r.receipt?.confirmed;
    if (filter === "pending") return !r.receipt?.confirmed;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="payroll-receipts-tab">
      <div className="grid grid-cols-3 gap-3">
        <KPI label="Paid Payroll Records" value={data.total} />
        <KPI label="Receipts Confirmed" value={data.confirmed} tone="emerald" />
        <KPI label="Awaiting Receipt" value={data.pending} tone="amber" />
      </div>

      <div className="flex items-center gap-2">
        {["all", "pending", "confirmed"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition ${filter === f ? "bg-[#0f1e42] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid={`receipt-filter-${f}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div className="dc-card overflow-hidden">
        {loading ? <Loading /> : filtered.length === 0 ? <Empty text="No payroll records match." /> : (
          <table className="w-full">
            <thead className="bg-slate-50 text-left text-[11px] font-mono-alt uppercase tracking-widest text-slate-500">
              <tr><th className="px-5 py-3">Employee</th><th className="px-5 py-3">Period</th>
                  <th className="px-5 py-3">Net Paid</th><th className="px-5 py-3">Paid On</th>
                  <th className="px-5 py-3">Receipt</th><th className="px-5 py-3">Method</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r) => (
                <tr key={r.id} className="text-sm cursor-pointer hover:bg-slate-50" onClick={() => setSelected(r)}>
                  <td className="px-5 py-3 font-semibold text-slate-900">{r.staff_name || staffName(r.staff_id)}</td>
                  <td className="px-5 py-3 font-mono-alt text-xs">{r.period_month}</td>
                  <td className="px-5 py-3 font-mono-alt tabular-nums font-semibold">{formatCurrency(r.net_amount)}</td>
                  <td className="px-5 py-3 text-xs text-slate-500">{r.paid_at ? formatDate(r.paid_at) : "—"}</td>
                  <td className="px-5 py-3"><ReceiptBadge req={{ status: "paid", receipt: r.receipt }} /></td>
                  <td className="px-5 py-3 text-xs text-slate-600">{r.receipt?.method?.replace(/_/g, " ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-md">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">Payroll Receipt</DialogTitle>
                <DialogDescription>{selected.staff_name || staffName(selected.staff_id)} · {selected.period_month}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Row label="Net Paid">{formatCurrency(selected.net_amount)}</Row>
                <Row label="Paid On">{selected.paid_at ? formatDate(selected.paid_at) : "—"}</Row>
              </div>
              <ReceiptConfirmBlock request={{ id: selected.id, status: "paid", receipt: selected.receipt }} onConfirmed={() => { setSelected(null); load(); }} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
